import dedent from 'ts-dedent'
import { MILES_TO_M } from '../const'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn } from '../utils/schemas'
import { SelectWorkflowComponent } from './common/selectcomponent'

class Simplify extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.simplify',
        group: 'Spatial Operations',
        title: 'ST Simplify',
        description: `
      This component adds a new column containing a simplified version of the original input geography.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'tolerance',
          type: WorkflowComponentParamType.Number,
          title: 'Tolerance',
          default: 1,
          min: 0
        },
        {
          name: 'units',
          type: WorkflowComponentParamType.Selection,
          title: 'Units',
          options: ['Meters', 'Miles'],
          default: 'Meters'
        }
      ]
    )
  }

  getInnerSql({ params, connection }: WorkflowComponentCodeParams) {
    const convert_to_m = params.units === 'Meters' ? '' : `* ${MILES_TO_M}`
    switch (connection.type) {
      case Provider.BigQuery:
      // for BQ do not need convertion because all geoms in BQ are
      // geography and API accept only geography
      case Provider.Snowflake:
        // for SF no need convertion SF accept both gemetry and geography
        return `SELECT ST_SIMPLIFY(
          ${params.geo},
          ${params.tolerance} ${convert_to_m}
        ) AS ${params.geo}_simplified, *
        FROM ${connection.quoteName(params.source as string)}`
      case Provider.Postgres:
      case Provider.Redshift:
        return `SELECT
                  ST_TRANSFORM(
                    ST_SIMPLIFY(
                      ST_TRANSFORM(${params.geo}, 3857),
                      (${params.tolerance} ${convert_to_m})
                    ),
                    ST_SRID(${params.geo})
                  ) AS ${params.geo}_simplified,
                  *
                FROM
                  ${connection.quoteName(params.source as string)}`
      case Provider.DatabricksRest:
        return dedent`SELECT /*+ REPARTITION(40, ${params.geo}_simplified) */
                  SEDONA_ST_ASBINARY(
                    SEDONA_ST_TRANSFORM(
                      SEDONA_ST_SETSRID(
                        SEDONA_ST_SIMPLIFYPRESERVETOPOLOGY(
                          SEDONA_ST_TRANSFORM(${
                            params.geo
                          }_processed, 'epsg:3857'),
                          (${params.tolerance} ${convert_to_m})
                        )
                      , 3857)
                    , 'epsg:4326'
                    )
                  ) AS ${params.geo}_simplified,
                  * EXCEPT(${params.geo}_processed)
                FROM (
                  SELECT /*+ REPARTITION(40) */ *, SEDONA_ST_SETSRID(
                    SEDONA_ST_GEOMFROMWKB(${params.geo})
                    , 4326) AS ${params.geo}_processed
                  FROM ${connection.quoteName(params.source as string)}
                )`
      default:
        return null
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({
    columns,
    params,
    connection
  }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: `${params.geo}_simplified`,
      type: connection.getGeoDataType()
    })
  }
}

export { Simplify }
